'use client';

import React from 'react';
import { CheckCircleIcon, CrossIcon } from '@/components/Icons/Icons';
import { Question, AssessmentResult } from '../types';
import { QuizItem } from '@/store/assessment-store';

interface QuestionsListProps {
  questions: Question[];
  questionIndex: number;
  quiz: QuizItem[];
  result?: AssessmentResult | null;
  getQuestionCorrectness?: (questionUid: string) => boolean | null; // Function to determine if question is correct
  onQuestionClick: (index: number) => void;
  type?: 'quiz' | 'assessment'; // Add type to distinguish quiz from assessment
}

export function QuestionsList({
  questions,
  questionIndex,
  quiz,
  result,
  getQuestionCorrectness,
  onQuestionClick,
  type = 'quiz',
}: QuestionsListProps) {
  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    // QuestionsList data logged in development
  }

  if (!questions || questions.length === 0) {
    return (
      <div className="cs-caption text-text-default-medium py-2">
        No questions available
      </div>
    );
  }

  // Create a map of question UIDs to their correctness status
  const questionCorrectnessMap = React.useMemo(() => {
    const map: Record<string, boolean | null> = {};
    const quizArray = Array.isArray(quiz) ? quiz : [];
    const isResultsMode = !!result;

    questions.forEach((question) => {
      const quizItem = quizArray.find((q) => q?.question_uid === question.metadata_uid);
      const hasAnswered = !!quizItem || isResultsMode;

      if (hasAnswered && getQuestionCorrectness) {
        map[question.metadata_uid] = getQuestionCorrectness(question.metadata_uid);
      } else if (quizItem) {
        map[question.metadata_uid] = getQuestionCorrectness
          ? getQuestionCorrectness(question.metadata_uid)
          : null;
      }
    });

    return map;
  }, [questions, quiz, result, getQuestionCorrectness]);

  // Ensure quiz is always an array
  const quizArray = Array.isArray(quiz) ? quiz : [];

  return (
    <div className="flex flex-col max-h-[300px] overflow-y-auto scrollbar-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
      {questions.map((item, i) => {
        const quizItem = quizArray.find((q) => q?.question_uid === item.metadata_uid);
        const isResultsMode = !!result;
        const isAnswered = !!quizItem || isResultsMode;
        const isCurrent = questionIndex === i;

        const correctness = questionCorrectnessMap[item.metadata_uid];
        // Assessment: show neutral icon when answered. Quiz results: show correct/incorrect. Quiz taking: show neutral icon when answered (like assessment), no correct/wrong until submitted
        const showIcon =
          type === 'assessment'
            ? isAnswered
            : isAnswered && (isResultsMode ? correctness === true || correctness === false : true);

        // Navigation logic:
        // - In results mode: Always allow navigation
        // - For assessments: Always allow navigation
        // - For quiz (during taking): Only allow navigation to answered questions or current question
        const isClickable = result 
          ? true // Results mode: always clickable
          : type === 'assessment' 
            ? true // Assessment: always clickable
            : isAnswered || isCurrent; // Quiz: only answered questions or current question
        
        return (
          <button
            key={i}
            onClick={() => {
              if (isClickable) {
                onQuestionClick(i);
              }
            }}
            disabled={!isClickable}
            className={`outline-focussed flex items-center justify-between gap-2 py-3 px-6 rounded-lg text-left transition-all w-full ${
              isClickable ? 'question-list-item-hover cursor-pointer' : 'cursor-default opacity-60'
            } ${
              isCurrent ? 'question-list-item-active' : ''
            }`}
            type="button"
            role="button"
            aria-label={`Question ${i + 1}${isAnswered ? (correctness === true ? ' - Correct' : correctness === false ? ' - Incorrect' : ' - Answered') : ' - Not answered'}${isCurrent ? ' - Current' : ''}${!isClickable ? ' - Answer this question first' : ''}`}
          >
            <span className={`cs-h6 ${isCurrent ? 'font-semibold' : ''}`}>
              Question {i + 1}
            </span>
            {showIcon && (
              <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
                {type === 'assessment' || (type === 'quiz' && !result) ? (
                  <CheckCircleIcon size={16} className="text-text-default-medium" aria-hidden />
                ) : correctness === true ? (
                  <CheckCircleIcon size={16} className="text-green" aria-hidden />
                ) : correctness === false ? (
                  <CrossIcon className="h-4 w-4 text-red" aria-hidden />
                ) : null}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
